import { apiClient } from './api';
import type { ApiResponse } from '@/types';

export type FlowStatus = 'draft' | 'active' | 'paused';
export type FlowTriggerType = 'inbound' | 'schedule';
export type FlowChannel = 'whatsapp' | 'webchat';

export interface FlowTriggerInbound {
  type: 'inbound';
  channels: FlowChannel[];
}

export interface FlowTriggerSchedule {
  type: 'schedule';
  timezone: string;
  cron: string;
}

export type FlowTrigger = FlowTriggerInbound | FlowTriggerSchedule;

export type FlowStepType =
  | 'ai_interpret'
  | 'tool_sql'
  | 'tool_api'
  | 'send_message'
  | 'condition'
  | 'delay'
  | 'end';

export interface FlowStep {
  id: string;
  type: FlowStepType;
  name?: string;
  params?: Record<string, any>;
}

export interface Flow {
  id: string;
  name: string;
  status: FlowStatus;
  trigger_type: FlowTriggerType;
  trigger_config: Record<string, any>;
  steps: FlowStep[];
  updated_at: string;
  // Campos legados para compatibilidade
  trigger?: FlowTrigger;
}

export interface CreateFlowRequest {
  name: string;
  status?: FlowStatus;
  trigger_type: FlowTriggerType;
  trigger_config?: Record<string, any>;
  steps: FlowStep[];
  variables?: Record<string, any>;
}

export interface UpdateFlowRequest {
  name?: string;
  status?: FlowStatus;
  trigger_config?: Record<string, any>;
  steps?: FlowStep[];
  variables?: Record<string, any>;
}

export interface TestFlowRequest {
  channel: FlowChannel;
  input:
    | { type: 'text'; text: string }
    | { type: 'audio'; media_id: string; transcription?: string };
}

export interface FlowTraceStep {
  step_id: string;
  type: FlowStepType;
  status: 'ok' | 'skipped' | 'error';
  input?: any;
  output?: any;
  error?: string;
}

export interface TestFlowResponse {
  trace: FlowTraceStep[];
  result?: {
    message?: string;
    channel?: FlowChannel;
  };
}

export async function listFlows(): Promise<ApiResponse<Flow[]>> {
  return apiClient.get<Flow[]>('/flows');
}

export async function getFlow(id: string): Promise<ApiResponse<Flow>> {
  return apiClient.get<Flow>(`/flows/${id}`);
}

export async function createFlow(payload: CreateFlowRequest): Promise<ApiResponse<Flow>> {
  return apiClient.post<Flow>('/flows', payload);
}

export async function updateFlow(id: string, payload: UpdateFlowRequest): Promise<ApiResponse<Flow>> {
  return apiClient.put<Flow>(`/flows/${id}`, payload);
}

export async function deleteFlow(id: string): Promise<ApiResponse<{ ok: true }>> {
  return apiClient.delete<{ ok: true }>(`/flows/${id}`);
}

export async function testFlow(id: string, payload: TestFlowRequest): Promise<ApiResponse<TestFlowResponse>> {
  return apiClient.post<TestFlowResponse>(`/flows/${id}/test`, payload);
}
